import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { RankingCbosConsultasEletivas } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { GeralService, NotificationService, TipoDominio, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { CellStyle, ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import {
  AgFilterUtils,
  buildAutoGroupColumnDef,
  buildDefaultColumnDef,
  buildGridOptions,
  exportGridAsExcel,
} from '@gestor-web/app/shared/components';
import { SelectItem } from 'primeng/api';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, first, map, mergeMap, takeUntil } from 'rxjs/operators';
import { FilterData } from '../../components/filtro/filtro.component';
import { RankingCboConsultasService } from './ranking-cbo-consultas.service';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'ANALISES.RANKING_CBO_CONSULTAS';

const COLUMNS: ColDef[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    type: 'competencia',
  },
  {
    headerName: 'Nr. ranking',
    field: 'nr_ranking_cbo',
    type: 'int',
    cellStyle: { textAlign: 'center' },
    width: 100,
    sort: 'asc',
  },
  {
    headerName: 'Nr. ranking CBO competência',
    field: 'nr_ranking_cbo_competencia',
    type: 'int',
  },
  {
    headerName: '035-Cód. CBO executante',
    field: 'cbo_especialidade',
    cellStyle: { textAlign: 'center' },
  },
  {
    headerName: 'CBO do Executante na Guia',
    field: 'titulo_cbo_especialidade',
  },
  {
    headerName: 'Qtde de guia',
    field: 'qtde_guia',
    type: 'int',
  },
  {
    headerName: 'Valor informado',
    field: 'vl_informado',
    type: 'money',
  },
  {
    headerName: 'Valor médio',
    field: 'vl_medio',
    type: 'money',
  },
  {
    headerName: 'Valor médio CBO competência',
    field: 'vl_medio_cbo_competencia',
    type: 'int',
  },
  {
    headerName: 'Qtde médico identificado',
    field: 'qtde_medico_identificado',
    type: 'int',
  },
  {
    headerName: 'Qtde médico não identificado',
    field: 'qtde_medico_nao_identificado',
    type: 'int',
  },
  {
    headerName: 'Elegibilidade do CBO para o indicador',
    field: 'nm_id_elegibilidade_cbo',
  },
  {
    headerName: 'ID elegibilidade do CBO para o indicador',
    field: 'id_elegibilidade_cbo',
    hide: true,
  },
];

@Component({
    templateUrl: './ranking-cbo-consultas.component.html',
    styleUrls: ['./ranking-cbo-consultas.component.scss'],
    providers: [RankingCboConsultasService, DecimalPipe],
    standalone: false
})
export class RankingCboConsultasComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<Row>;

  gridOptions: GridOptions<Row>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];

  anosBasesOptions: number[] = [];
  anoBase: number = null;

  faixasEtariasOptions: SelectItem<number>[] = [];
  faixaEtaria: number = null;

  unidadeOptions: SelectItem<number>[] = [];
  selectedUnidades: number[] = [];
  hasUnidades = false;

  filterData: FilterData = {
    nr_cnpj_cpf_exec: null,
    tp_rede: null,
    id_origem_guia: null,
  };

  rowData: Row[];

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private permissaoService: PermissaoService,
    private service: RankingCboConsultasService,
    private geralService: GeralService,
    private decimalPipe: DecimalPipe,
    private competenciaPipe: CompetenciaPipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        distinctUntilChanged((prev, curr) => prev?.cd_registro_ans === curr?.cd_registro_ans),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        mergeMap(([operadora, params]) =>
          this.service.getAnoBase(operadora.cd_registro_ans).pipe(map(anosBases => ({ operadora, params, anosBases }))),
        ),
        mergeMap(({ operadora, params, anosBases }) =>
          this.geralService
            .getAnoBaseVigente()
            .pipe(map(anoBaseVigente => ({ operadora, params, anosBases, anoBaseVigente }))),
        ),
        mergeMap(({ operadora, params, anosBases, anoBaseVigente }) =>
          this.geralService
            .getDominioPorTipo(TipoDominio.IND_FAIXA_ETARIA_INDICADOR_CONSULTA)
            .pipe(map(faixasEtarias => ({ operadora, params, anosBases, anoBaseVigente, faixasEtarias }))),
        ),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(({ operadora, params, anosBases, anoBaseVigente, faixasEtarias }) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : null,
          params.competencia_final ? '' + params.competencia_final : null,
        ];

        this.anoBase = anoBaseVigente;

        if (anosBases && anosBases.length > 0) {
          this.anosBasesOptions = anosBases;
        } else {
          this.anosBasesOptions = [anoBaseVigente];
        }

        this.faixasEtariasOptions = faixasEtarias.map(item => ({
          value: +item.cd_valor_valido,
          label: item.ds_exibicao,
        }));
        this.faixaEtaria = this.faixasEtariasOptions.length ? this.faixasEtariasOptions[0].value : null;

        this.service.getUnidadesOperadora(this.operadora).subscribe(unidades => {
          if (unidades.length > 0) {
            this.hasUnidades = true;
            this.unidadeOptions = [
              { label: this.operadoraNome, value: 0 },
              ...unidades.map(u => ({ label: u.nm_unidade_negocio, value: +u.cd_unidade })),
            ];
          } else {
            this.hasUnidades = false;
            this.unidadeOptions = [];
            this.selectedUnidades = [];
          }
        });

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<Row>(
      {
        columnDefs: COLUMNS,
        pipes: {
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        // <Row> explicito: sem ele o ag-grid 36 estoura a profundidade de inferencia (TS2589)
        defaultColDef: buildDefaultColumnDef<Row>({
          cellClassRules: {
            'cell-green': params => !params.api.isPivotMode() && +params?.data?.id_elegibilidade_cbo?.charAt(0) == 1,
            'cell-white': params => !params.api.isPivotMode() && +params?.data?.id_elegibilidade_cbo?.charAt(0) == 2,
            'cell-yellow': params => !params.api.isPivotMode() && +params?.data?.id_elegibilidade_cbo?.charAt(0) == 3,
            'cell-red': params =>
              !params.api.isPivotMode() && Math.floor(+params?.data?.id_elegibilidade_cbo / 10) >= 4,
          },
        }),
        autoGroupColumnDef: buildAutoGroupColumnDef<Row>({
          cellStyle: params => {
            const colGettingGrouped = params.colDef.showRowGroup as string;
            if (colGettingGrouped == 'nm_id_elegibilidade_cbo') {
              const style: CellStyle = {
                backgroundColor: this.getPivotBackgroundColor(params),
              };
              return style;
            }
            return null;
          },
        }),
        excelStyles: [
          { id: 'cell-green', interior: { color: '#e2efda', pattern: 'Solid' } },
          { id: 'cell-white', interior: { color: '#ffffff', pattern: 'None' } },
          { id: 'cell-yellow', interior: { color: '#ffe699', pattern: 'Solid' } },
          { id: 'cell-red', interior: { color: '#ff8181', pattern: 'Solid' } },
        ],
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, anoBase, faixaEtaria, visao, sort, extraFilters, ...filterData } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (anoBase) {
      hasFilter = true;
      this.anoBase = +anoBase;
    }

    if (faixaEtaria) {
      this.faixaEtaria = +faixaEtaria;
    }

    if (filterData && Object.keys(filterData).length) {
      hasFilter = true;
    }
    this.filterData = { ...this.filterData, ...filterData };

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    // Remove filters from url
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    this.filterChanged = false;

    // Trata o comportamento do componente visual do carregamento
    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.competenciaRange[0] || !this.competenciaRange[1] || !this.anoBase) {
      this.rowData = [];
      this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
      return;
    }

    const cdUnidadeList = this.selectedUnidades.length ? this.selectedUnidades.join(',') : undefined;

    this.service
      .getData(
        this.operadora,
        this.competenciaRange[0],
        this.competenciaRange[1],
        this.anoBase,
        this.faixaEtaria,
        this.filterData,
        cdUnidadeList,
      )
      .subscribe({
        next: items => {
          if (!items?.length) {
            this.rowData = [];
            this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
            return;
          }

          this.rowData = items.map(item => {
            const row = {
              ...item,
              nm_id_elegibilidade_cbo: '',
            };

            row.nm_id_elegibilidade_cbo = this.geralService.buildNomeDeDominio(
              row.id_elegibilidade_cbo,
              'CAD_CBO_ELEGIBILIDADE',
            );

            return row;
          });

          const totalData = items.reduce(
            (acc, item) => ({
              nr_ranking_cbo: null,
              cbo_especialidade: null,
              titulo_cbo_especialidade: null,
              id_elegibilidade_cbo: null,
              qtde_guia: acc.qtde_guia + item.qtde_guia,
              vl_informado: acc.vl_informado + item.vl_informado,
              vl_medio: acc.vl_medio + item.vl_medio,
              qtde_medico_identificado: acc.qtde_medico_identificado + item.qtde_medico_identificado,
              qtde_medico_nao_identificado: acc.qtde_medico_nao_identificado + item.qtde_medico_nao_identificado,
            }),
            {
              qtde_guia: 0,
              vl_medio: 0,
              vl_informado: 0,
              qtde_medico_identificado: 0,
              qtde_medico_nao_identificado: 0,
            },
          );

          this.agGrid?.api?.setGridOption('pinnedBottomRowData', [totalData]);
        },
        error: error => this.notificationService.handleError(error),
        complete: () => this.agGrid?.api?.setGridOption('loading', false),
      });
  }

  getPivotBackgroundColor(params) {
    // TODO: verificar possibilidade mudar pra switch
    const id = params.value?.charAt(0);
    if (id == 1) {
      return '#e2efda';
    } else if (id == 2) {
      return '#ffffff';
    } else if (id == 3) {
      return '#ffe699';
    } else if (id >= 4 || +params.value === 0) {
      return '#ff8181';
    }
    return null;
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    // this.reloadData();
    this.filterChanged = true;
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  exportExcel(): void {
    const fileNamePrefix = `Ranking-CBOs-Consultas-Eletivas-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const headers = [
      'Ranking dos CBOs de consultas eletivas',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
      'Ano de realização: ' + this.anoBase,
      'Faixa etária: ' + this.faixasEtariasOptions.find(faixa => faixa.value == this.faixaEtaria).label,
    ];

    if (this.selectedUnidades.length) {
      const nomes = this.selectedUnidades.map(cd => this.unidadeOptions.find(un => un.value == cd)?.label).join(', ');
      headers.push('Unidade de negócio: ' + nomes);
    }

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      ...this.filterData,
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      anoBase: this.anoBase,
      faixaEtaria: this.faixaEtaria,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

interface Row extends RankingCbosConsultasEletivas {
  nm_id_elegibilidade_cbo: string;
}
